import ArchiveIcon from '@mui/icons-material/Archive';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import type { ReactNode } from 'react';
import { PATH } from '@siloreal:app:config';
import type { SilobagListLogicState } from '@siloreal:modules:private/silobag-list/config/context';
import type { SilobagRow } from '@siloreal:modules:private/silobag-list/config/extra/types';
import type { RouteNavigate } from '@siloreal:shared:hooks/controller/route/use-route-controller';
import * as HOOKS from '@siloreal:shared:hooks';

export type RowAction = {
  key: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tooltip?: string;
};

type RowActionContext = {
  row: SilobagRow;
  state: SilobagListLogicState;
  navigate: RouteNavigate<
    Record<string, never>,
    { isGuest?: boolean; pathSilobags?: boolean; siloListId?: string[] }
  >;
  siloListId?: string[];
  openTagModal: () => void;
  downloadCertificate: () => void;
  isDownloadingCertificate: boolean;
  archiveSilobag: () => void;
  isArchivingSilobag: boolean;
  closeSilobag: () => void;
  extractSilobag: () => void;
};

type RowActionBuilder = (ctx: RowActionContext) => RowAction;

const VIEW_DETAIL: RowActionBuilder = ({ row, state, navigate, siloListId }) => ({
  key: 'view-detail',
  label:
    state.view === 'silospro'
      ? HOOKS.useLocale.getState().t('temp.private.list.silospro.view_silopro')
      : HOOKS.useLocale.getState().t('temp.private.list.silobag.view_silobag'),
  icon: <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />,
  onClick: () => {
    navigate.visit(`${PATH.SILOBAG}/${row.id}`, {
      state: { pathSilobags: true, siloListId },
    });
  },
});

const VIEW_MAP: RowActionBuilder = ({ row, navigate }) => ({
  key: 'view-map',
  label: HOOKS.useLocale.getState().t('temp.private.list.common.view_in_map'),
  icon: <PlaceOutlinedIcon sx={{ fontSize: 18 }} />,
  onClick: () => {
    navigate(`${PATH.MAP}/${row.id}`);
  },
});

const EDIT: RowActionBuilder = ({ row, state, navigate }) => ({
  key: 'edit',
  label:
    state.view === 'silospro'
      ? HOOKS.useLocale.getState().t('temp.private.list.silospro.edit_silopro')
      : HOOKS.useLocale.getState().t('temp.private.list.silobag.edit_silobag'),
  icon: <EditOutlinedIcon sx={{ fontSize: 18 }} />,
  onClick: () => {
    navigate.visit(`${PATH.SILOBAG}/${row.id}/edit`, {
      state: { isGuest: row.is_guest },
    });
  },
  disabled: !row.write_permission,
});

const ASSIGN_TAGS: RowActionBuilder = ({ row, openTagModal }) => ({
  key: 'assign-tags',
  label: HOOKS.useLocale.getState().t('temp.common.tagging'),
  icon: <SellOutlinedIcon sx={{ fontSize: 18 }} />,
  onClick: openTagModal,
  disabled: row.is_guest,
});

function createStaticAction(
  key: string,
  label: string,
  icon: ReactNode,
  comingSoon = false
): RowActionBuilder {
  const COMING_SOON_TOOLTIP = HOOKS.useLocale.getState().t('temp.common.coming_soon');
  return () => ({
    key,
    label,
    icon,
    onClick: () => undefined,
    disabled: comingSoon,
    tooltip: comingSoon ? COMING_SOON_TOOLTIP : undefined,
  });
}

const INSURE_SILOS_PRO = createStaticAction(
  'insure-silos-pro',
  HOOKS.useLocale.getState().t('temp.private.list.silospro.insure_your_silospro'),
  <GppGoodOutlinedIcon sx={{ fontSize: 18 }} />,
  true
);

const QUOTE_IWARRANT = createStaticAction(
  'quote-iwarrant',
  HOOKS.useLocale.getState().t('temp.private.list.common.quote_iwarrant'),
  <WorkspacePremiumIcon sx={{ fontSize: 18 }} />,
  true
);

const EXTRACT: RowActionBuilder = ({ row, extractSilobag }) => ({
  key: 'extract',
  label: HOOKS.useLocale.getState().t('temp.common.perform_extraction'),
  icon: <ArchiveIcon sx={{ fontSize: 18 }} />,
  onClick: extractSilobag,
  disabled: !row.write_permission || !row.active,
});

const CLOSE_SILOBAG: RowActionBuilder = ({ state, closeSilobag }) => ({
  key: 'close-silobag',
  label:
    state.view === 'silospro'
      ? HOOKS.useLocale.getState().t('temp.private.list.silospro.close_silopro')
      : HOOKS.useLocale.getState().t('temp.private.list.silobag.close_silobag'),
  icon: <CancelIcon sx={{ fontSize: 18 }} />,
  onClick: closeSilobag,
});

const ARCHIVE_SILOBAG: RowActionBuilder = ({ row, archiveSilobag, isArchivingSilobag }) => ({
  key: 'archive-silobag',
  label: HOOKS.useLocale.getState().t('temp.common.archive_silobag'),
  icon: <Inventory2OutlinedIcon sx={{ fontSize: 18 }} />,
  onClick: archiveSilobag,
  disabled: !row.write_permission || isArchivingSilobag,
});

const DOWNLOAD_CERTIFICATE: RowActionBuilder = ({ row, downloadCertificate, isDownloadingCertificate }) => ({
  key: 'download-certificate',
  label: `${HOOKS.useLocale.getState().t('temp.common.download_certificate')} ${row.id}`,
  icon: <DownloadOutlinedIcon sx={{ fontSize: 18 }} />,
  onClick: downloadCertificate,
  disabled: row.silobag_data_statuses?.id !== 2 || isDownloadingCertificate,
});

/**
 * Acciones del menú `⋮` por tabla.
 * Agregar builders acá a medida que se sumen opciones por vista.
 */
export const TABLE_ROW_ACTIONS = {
  SILOS_PRO: {
    VRR: [
      VIEW_DETAIL,
      VIEW_MAP,
      EDIT,
      ASSIGN_TAGS,
      INSURE_SILOS_PRO,
      QUOTE_IWARRANT,
      EXTRACT,
      CLOSE_SILOBAG,
      DOWNLOAD_CERTIFICATE,
    ],
    WITHOUT_VRR: [VIEW_DETAIL, VIEW_MAP, EDIT, ASSIGN_TAGS, EXTRACT, CLOSE_SILOBAG, DOWNLOAD_CERTIFICATE],
    CLOSED: [VIEW_DETAIL, ASSIGN_TAGS, DOWNLOAD_CERTIFICATE],
  },
  SILOBAG: {
    ACTIVE: [VIEW_DETAIL, EDIT, ASSIGN_TAGS, EXTRACT, CLOSE_SILOBAG, ARCHIVE_SILOBAG],
    CLOSED: [VIEW_DETAIL, ASSIGN_TAGS],
    ARCHIVED: [],
  },
} as const;

const SILOS_PRO_TAB_KEY = {
  vrr: 'VRR',
  'no-vrr': 'WITHOUT_VRR',
  closed: 'CLOSED',
  active: 'ACTIVE',
  archived: 'ARCHIVED',
} as const;

export function resolveRowActionBuilders(state: SilobagListLogicState): readonly RowActionBuilder[] {
  if (state.view === 'silospro') return TABLE_ROW_ACTIONS.SILOS_PRO[SILOS_PRO_TAB_KEY[state.tab]];
  if (state.view === 'silobag') return TABLE_ROW_ACTIONS.SILOBAG[SILOS_PRO_TAB_KEY[state.tab]];
  return [];
}

export function buildRowActions(ctx: RowActionContext): RowAction[] {
  return resolveRowActionBuilders(ctx.state).map((build) => build(ctx));
}
